import type { ComponentType } from "react";
import {
  BookOpen,
  Users,
  Inbox,
  ChartNoAxesCombined,
  Route,
} from "lucide-react";
import type { PlanFeatures } from "@/config/stripePlans";

// Icons are either lucide icons or our own SVG marks. Both render
// from a single `className` prop, so we type to the common shape.
export type SidebarIcon = ComponentType<{ className?: string }>;

export type SidebarItem = {
  key: string;              // stable id, also used as pin identifier
  label: string;            // visible text
  section: string;          // ?tab= value (usually === key)
  icon: SidebarIcon;
  hasPreviewIcon?: boolean; // only true for Storefront
  children?: SidebarItem[]; // nested sub-items
  requiresFeature?: keyof PlanFeatures; // mark as locked and open with the in-feature upgrade overlay
  /** Extra query params merged into the URL alongside ?tab= when navigating to this item. */
  sectionParams?: Record<string, string>;
};

export type SidebarGroup = {
  key: "overview" | "connected" | "dibs-bookings";
  label: string;
  collapsible?: boolean;
  items: SidebarItem[];
};

// Keep the primary destinations in one flat list beneath the branch selector.
// Home and other hidden sections remain routable in Admin.tsx, but are
// intentionally not shown as top-level sidebar destinations.
export const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    key: "overview",
    label: "",
    items: [
      { key: "inbox", label: "Inbox", section: "inbox", icon: Inbox },
      { key: "customers", label: "Customers", section: "customers", icon: Users },
      { key: "analytics", label: "Analytics", section: "analytics", icon: ChartNoAxesCombined },
      { key: "workflows", label: "Workflows", section: "workflows", icon: Route, requiresFeature: "automations" },
      { key: "knowledge", label: "Knowledge", section: "knowledge", icon: BookOpen },
    ],
  },
];

/** Flat list of all sidebar items (including nested children) for quick lookup */
export const ALL_SIDEBAR_ITEMS: SidebarItem[] = SIDEBAR_GROUPS.flatMap((g) =>
  g.items.flatMap((i) => (i.children ? [i, ...i.children] : [i])),
);

/**
 * Sub-pages that aren't their own sidebar item but should keep a top-level item
 * highlighted while open. Maps a `?tab=` section to the owning item `key`.
 */
export const SECTION_PARENT_KEY: Record<string, string> = {
  "audit-logs": "home",
  "conversations": "inbox",
  "customer-details": "customers",
  "customer-import": "customers",
};
